import { useMemo } from "react";

import { Box, SimpleGrid } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { DECIMAL_PROPS, NumberInput, RadioGroup, YesNoRadioButton } from "components";
import SectionTitle from "pages/clients/view/components/section-title";
import { formClasses } from "styles";
import { BankCreditOptionsResponse, BankCreditRequestForm, BankCreditResponse } from "types";

import { BankCreditRequestPersonalChildrenIndividual } from "./bank-credit-children-individual";

interface CreditRequestPersonalChildrenProps {
  credit: BankCreditResponse;
  options: BankCreditOptionsResponse;
  form: UseFormReturnType<BankCreditRequestForm>;
  visibleFields: string[];
  sourceChildBirthYears?: Array<number | undefined>;
  readOnly?: boolean;
}

export const BankCreditRequestPersonalChildren = ({
  credit,
  options,
  form,
  visibleFields,
  sourceChildBirthYears,
  readOnly = false,
}: CreditRequestPersonalChildrenProps) => {
  const { t } = useTranslation();

  const childrenLiveWithYouOptions = useMemo(
    () =>
      options["childrenLiveWithYou"].map((el) => ({
        value: el,
        label: t(`bankCredit.childrenLiveWithYou.options.${el}`),
      })),
    [options, t],
  );

  return (
    <Box className={formClasses.sectionBorder}>
      <SectionTitle label={t("credit.children.label")} isBankCredit />
      <SimpleGrid cols={4}>
        <YesNoRadioButton
          label={t("credit.supportsChildren.label")}
          name={"supportsChildren"}
          form={form}
          disabled={readOnly}
        />
        {visibleFields.includes("childrenLiveWithYou") && (
          <RadioGroup
            label={t("bankCredit.childrenLiveWithYou.label")}
            fieldName={"childrenLiveWithYou"}
            form={form}
            disabled={readOnly}
            options={childrenLiveWithYouOptions}
          />
        )}
        {visibleFields.includes("childrenInAbroad") && (
          <YesNoRadioButton
            label={t("bankCredit.childrenInAbroad.label")}
            name={"childrenInAbroad"}
            form={form}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("paysAlimony") && (
          <YesNoRadioButton
            label={t("bankCredit.paysAlimony.label")}
            name={"paysAlimony"}
            form={form}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      <SimpleGrid cols={4} mt="md">
        {visibleFields.includes("foreignTransferAmount") && (
          <NumberInput
            label={t("bankCredit.foreignTransferAmount.label")}
            key={form.key("foreignTransferAmount")}
            {...form.getInputProps("foreignTransferAmount")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("monthlyAlimonyExpensesBn") && (
          <NumberInput
            label={t("bankCredit.monthlyAlimonyExpensesBn.label")}
            key={form.key("monthlyAlimonyExpensesBn")}
            {...form.getInputProps("monthlyAlimonyExpensesBn")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("monthlyAlimonyExpensesCb") && (
          <NumberInput
            label={t("bankCredit.monthlyAlimonyExpensesCb.label")}
            key={form.key("monthlyAlimonyExpensesCb")}
            {...form.getInputProps("monthlyAlimonyExpensesCb")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
        )}
        {visibleFields.includes("monthlyAlimonyExpensesCc") && (
          <NumberInput
            label={t("bankCredit.monthlyAlimonyExpensesCc.label")}
            key={form.key("monthlyAlimonyExpensesCc")}
            {...form.getInputProps("monthlyAlimonyExpensesCc")}
            min={0}
            {...DECIMAL_PROPS}
            disabled={readOnly}
          />
        )}
      </SimpleGrid>

      <BankCreditRequestPersonalChildrenIndividual
        credit={credit}
        form={form}
        options={options}
        visibleFields={visibleFields}
        sourceChildBirthYears={sourceChildBirthYears}
        readOnly={readOnly}
      />
    </Box>
  );
};
